import type { SvgIconComponent } from "@mui/icons-material";
import AccountTree from "@mui/icons-material/AccountTree";
import Apps from "@mui/icons-material/Apps";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import DashboardCustomize from "@mui/icons-material/DashboardCustomize";
import Forum from "@mui/icons-material/Forum";
import Home from "@mui/icons-material/Home";
import Lightbulb from "@mui/icons-material/Lightbulb";
import People from "@mui/icons-material/People";
import Security from "@mui/icons-material/Security";
import Settings from "@mui/icons-material/Settings";
import Storage from "@mui/icons-material/Storage";

/** Stable kebab-case tab ids sent by the backend in `access.tabs`. */
export type NavTabName =
  | "home"
  | "data-hub"
  | "users"
  | "roles"
  | "chats"
  | "apps"
  | "app-studio"
  | "nuggets"
  | "actions"
  | "settings"
  | "context-hub";

export type NavTab = {
  /** Backend `access.tabs[].name`; used to match what the user may see. */
  name: NavTabName;
  /** Existing app route this tab navigates to. */
  route: string;
  /** `end` flag for the active-state route match (mirrors the prior sidebar behavior). */
  matchEnd: boolean;
  /** i18n key for the visible label/tooltip (display text stays on i18n). */
  labelKey: string;
  /** MUI icon rendered in the nav item. */
  icon: SvgIconComponent;
  /** Where the tab renders: the scrollable main list or the pinned footer. */
  placement: "main" | "footer";
};

/**
 * Single source of truth mapping each tab `name` to its route, icon, and label.
 * The backend only sends `name` + `label`; visibility and order come from
 * `access.tabs`, while this config supplies the repo-side route/icon/label.
 */
export const NAV_TABS: readonly NavTab[] = [
  { name: "home", route: "/", matchEnd: true, labelKey: "sidebar.home", icon: Home, placement: "main" },
  { name: "data-hub", route: "/data-hub", matchEnd: true, labelKey: "sidebar.dataHub", icon: Storage, placement: "main" },
  { name: "users", route: "/users", matchEnd: true, labelKey: "sidebar.users", icon: People, placement: "main" },
  { name: "roles", route: "/roles", matchEnd: true, labelKey: "sidebar.roles", icon: Security, placement: "main" },
  { name: "chats", route: "/chat-history", matchEnd: true, labelKey: "sidebar.chats", icon: Forum, placement: "main" },
  { name: "apps", route: "/apps", matchEnd: false, labelKey: "sidebar.apps", icon: Apps, placement: "main" },
  { name: "app-studio", route: "/app-studio", matchEnd: true, labelKey: "sidebar.appStudio", icon: DashboardCustomize, placement: "main" },
  { name: "nuggets", route: "/nuggets", matchEnd: true, labelKey: "sidebar.nuggets", icon: Lightbulb, placement: "main" },
  { name: "actions", route: "/actions", matchEnd: true, labelKey: "sidebar.actions", icon: AutoFixHigh, placement: "main" },
  { name: "settings", route: "/settings", matchEnd: true, labelKey: "sidebar.settings", icon: Settings, placement: "footer" },
  { name: "context-hub", route: "/context-hub", matchEnd: true, labelKey: "sidebar.contextHub", icon: AccountTree, placement: "main" },
];

